import { useState } from "react";

interface Props {
  value: unknown;
}

const TRUNCATE_AT = 1500;

export function JsonViewer({ value }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(value, null, 2);
  const tooLong = json.length > TRUNCATE_AT;
  const display = tooLong && !expanded ? json.slice(0, TRUNCATE_AT) + "…" : json;
  const lineCount = json.split("\n").length;
  const sizeKb = (new Blob([json]).size / 1024).toFixed(1);

  async function copy() {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — algunos navegadores bloquean clipboard sin user gesture
    }
  }

  return (
    <div style={{ border: "1px solid var(--border-1)", borderRadius: 8, overflow: "hidden" }}>
      {/* Cabecera del bloque JSON: meta + acciones */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 10px",
          background: "var(--bg-1)",
          borderBottom: "1px solid var(--border-1)",
          fontSize: 10.5,
          fontFamily: "var(--font-mono)",
          color: "var(--fg-3)",
          letterSpacing: "0.02em",
        }}
      >
        <span>
          {lineCount} líneas · {sizeKb} KB
          {tooLong && !expanded && (
            <span style={{ marginLeft: 8, color: "var(--st-degraded)" }}>· vista parcial</span>
          )}
        </span>
        <button
          type="button"
          onClick={copy}
          style={{
            background: "transparent",
            border: "none",
            color: copied ? "var(--st-ok)" : "var(--fg-2)",
            fontSize: 10.5,
            fontFamily: "var(--font-mono)",
            cursor: "pointer",
            padding: "2px 6px",
            borderRadius: 4,
            transition: "color 0.15s",
          }}
          title="Copiar JSON al portapapeles"
        >
          {copied ? "✓ copiado" : "copiar"}
        </button>
      </div>

      {/* Pre con el JSON */}
      <pre
        style={{
          background: "var(--bg-2)",
          padding: 12,
          margin: 0,
          overflow: "auto",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          lineHeight: 1.55,
          maxHeight: expanded ? "none" : 320,
          color: "var(--fg-1)",
        }}
      >
        {display}
      </pre>

      {/* Botón expandir/colapsar — estilo "ghost link" centrado en su propio footer */}
      {tooLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            padding: "8px 12px",
            background: "var(--bg-1)",
            border: "none",
            borderTop: "1px solid var(--border-1)",
            color: "var(--accent)",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: "var(--font-display)",
            cursor: "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-2)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-1)";
          }}
        >
          {expanded ? "↑ Colapsar" : `↓ Ver ${lineCount - (display.split("\n").length - 1)} líneas más`}
        </button>
      )}
    </div>
  );
}
